import type {
  BulkCreationRow,
  BulkJobCreatedResponse,
  BulkJobListItem,
  BulkJobListResponse,
  BulkJobStatus,
  BulkJobSummaryCounts,
  BulkPositionSlug,
  BulkReportItem,
  PlacementAreaMm,
  PlacementMm,
  SizeTier,
} from '@designdeck/shared';
import apiClient, { ApiResponse, PaginatedResponse } from './client';

// ═══════════════════════════════════════════════════════════════════
// TYPES (mirror apps/api bulk-creation/dto/*.ts)
// ═══════════════════════════════════════════════════════════════════

export type {
  BulkCreationRow,
  BulkJobCreatedResponse,
  BulkJobListItem,
  BulkJobStatus,
  BulkReportItem,
};

/** Why a reference product cannot be picked in step 01. */
export type BulkReferenceProductDisabledReason = 'no_active_colors';

export interface BulkReferenceMethod {
  code: string;
  /** Localised method name. */
  name: string;
}

/** Master placement summary of a reference card. */
export interface BulkReferencePlacementSummary {
  /** Master product id. */
  productId: string;
  positionSlug: BulkPositionSlug;
  /** Effective size tier of the print area; null without mm. */
  sizeTier: SizeTier | null;
  /** Print area width in mm; null when the area has none. */
  widthMm: number | null;
  /** Motif width in mm from the stored placementMm, rounded. */
  designWidthMm: number | null;
}

/** A stored reference row, input of `fitToReference`. */
export interface BulkReferenceFitPlacement {
  /** Stored placement of the reference row in mm (R17). */
  placementMm: PlacementMm;
  /** Print area the placement lives on, in mm with its size tier. */
  area: PlacementAreaMm;
}

/**
 * Rows the "will be scaled down" hint fits new motifs against, per side:
 * the master product's row, else the first selectable product's row.
 */
export interface BulkReferenceFit {
  front: BulkReferenceFitPlacement | null;
  back: BulkReferenceFitPlacement | null;
}

/** One active colour of a reference product. */
export interface BulkReferenceProductColorOption {
  productColorId: string;
  /** Localised catalogue colour name. */
  name: string;
  /** Swatch colour as `#rrggbb`. */
  hexCode: string | null;
}

/**
 * Purchase (seller cost), selling price and margin of a reference product
 * in euros — one price per product (pricing model of 2026-09-19).
 */
export interface BulkReferenceProductPricing {
  /** Seller cost per piece (blank reference price plus decoration); null when a cost is not maintained. */
  purchase: number | null;
  /** The product's selling price. */
  selling: number;
  /** selling − purchase; null when the seller cost is unknown. */
  margin: number | null;
}

export interface BulkReferenceProductOption {
  productId: string;
  /** Localised catalogue product name. */
  name: string;
  /** The reference places a design on the back of this product. */
  hasBack: boolean;
  activeColorCount: number;
  disabledReason: BulkReferenceProductDisabledReason | null;
  /**
   * The active colours themselves, for the swatches in step 01. Capped by
   * the API; `activeColorCount` stays the authoritative total.
   */
  colors: BulkReferenceProductColorOption[];
  /** Null when the listing stores no price for this product. */
  pricing: BulkReferenceProductPricing | null;
}

export interface BulkReference {
  id: string;
  name: string;
  tags: string[];
  thumbnailUrl: string | null;
  publishedAt: string;
  methods: BulkReferenceMethod[];
  placement: BulkReferencePlacementSummary | null;
  fitReference: BulkReferenceFit;
  /** Selectable products (enabled in the listing, active in the catalogue). */
  productCount: number;
  products: BulkReferenceProductOption[];
}

export type BulkReferenceWindow = 7 | 30;
export type BulkReferenceSortField = 'publishedAt' | 'name';

export interface BulkReferencesQuery {
  search?: string;
  publishedWithinDays?: BulkReferenceWindow;
  sortBy?: BulkReferenceSortField;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface CreateBulkJobInput {
  referenceId: string;
  productIds: string[];
  rows: BulkCreationRow[];
  asDraft: boolean;
  /** One key per flow instance; a repeated key returns the same job. */
  idempotencyKey: string;
}

/** `GET /designs/bulk/jobs/:jobId`: job status plus one report page. */
export interface BulkJobDetail {
  job: {
    id: string;
    type: string;
    status: BulkJobStatus;
    createdAt: string;
    startedAt: string | null;
    finishedAt: string | null;
    summary: BulkJobSummaryCounts | null;
    jobError: { code: string; message: string } | null;
    itemCount: number;
  };
  items: BulkReportItem[];
  nextCursor: string | null;
}

// ═══════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

export async function getBulkReferences(
  query: BulkReferencesQuery = {}
): Promise<PaginatedResponse<BulkReference>> {
  const params = new URLSearchParams();
  const search = query.search?.trim();
  if (search) params.append('search', search);
  if (query.publishedWithinDays !== undefined) {
    params.append('publishedWithinDays', String(query.publishedWithinDays));
  }
  if (query.sortBy) params.append('sortBy', query.sortBy);
  if (query.sortOrder) params.append('sortOrder', query.sortOrder);
  if (query.page !== undefined) params.append('page', String(query.page));
  if (query.limit !== undefined) params.append('limit', String(query.limit));
  const qs = params.toString();
  const response = await apiClient.get<
    ApiResponse<PaginatedResponse<BulkReference>>
  >(`/designs/bulk/references${qs ? `?${qs}` : ''}`);
  return response.data.data;
}

export async function createBulkJob(
  input: CreateBulkJobInput
): Promise<BulkJobCreatedResponse> {
  const response = await apiClient.post<ApiResponse<BulkJobCreatedResponse>>(
    '/designs/bulk/jobs',
    input
  );
  return response.data.data;
}

export async function getBulkJobs(): Promise<BulkJobListItem[]> {
  const response =
    await apiClient.get<ApiResponse<BulkJobListResponse>>('/designs/bulk/jobs');
  return response.data.data.data;
}

export async function getBulkJob(
  jobId: string,
  cursor?: string,
  limit?: number
): Promise<BulkJobDetail> {
  const params = new URLSearchParams();
  if (cursor) params.append('cursor', cursor);
  if (limit) params.append('limit', limit.toString());
  const qs = params.toString();
  const response = await apiClient.get<ApiResponse<BulkJobDetail>>(
    `/designs/bulk/jobs/${encodeURIComponent(jobId)}${qs ? `?${qs}` : ''}`
  );
  return response.data.data;
}

export async function dismissBulkJob(jobId: string): Promise<{ id: string }> {
  const response = await apiClient.post<ApiResponse<{ id: string }>>(
    `/designs/bulk/jobs/${encodeURIComponent(jobId)}/dismiss`
  );
  return response.data.data;
}
